import { useState } from 'react';
import { useSelector } from 'react-redux';
import { RootState } from '../../store';
import { bookingEndpoints } from '../../services/api/endpoints/booking.endpoint';
import { CreateBookingDTO, UpdateBookingDTO } from '../../types/booking';
import { logger } from '../../utils/logger';

export const useBookingActions = () => {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const selectedOrganizationId = useSelector(
    (state: RootState) => state.organizationContext.selectedOrganizationId
  );

  const createBooking = async (data: CreateBookingDTO) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    setError(null);

    try {
      return await bookingEndpoints.createBooking(selectedOrganizationId, data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to create booking';
      setError(message);
      logger.error('Error creating booking:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const updateBooking = async (bookingId: number, data: UpdateBookingDTO) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    setError(null);

    try {
      return await bookingEndpoints.updateBooking(selectedOrganizationId, bookingId, data);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to update booking';
      setError(message);
      logger.error('Error updating booking:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteBooking = async (bookingId: number) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected');
    }

    setIsLoading(true);
    setError(null);

    try {
      await bookingEndpoints.deleteBooking(selectedOrganizationId, bookingId);
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete booking';
      setError(message);
      logger.error('Error deleting booking:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  const deleteRecurrenceBooking = async (recurrenceId: number, bookingId: number) => {
    if (!selectedOrganizationId) {
      throw new Error('No organization selected');
    }
    setIsLoading(true);
    setError(null);
    try {
      await bookingEndpoints.deleteRecurrenceBooking(
        selectedOrganizationId,
        recurrenceId,
        bookingId
      );
    } catch (err) {
      const message = err instanceof Error ? err.message : 'Failed to delete recurrence booking';
      setError(message);
      logger.error('Error deleting recurrence booking:', err);
      throw err;
    } finally {
      setIsLoading(false);
    }
  };

  return {
    createBooking,
    updateBooking,
    deleteBooking,
    deleteRecurrenceBooking,
    isLoading,
    error,
  };
};
